/**
 * VisitInfo Component
 *
 * This component serves as the main container for managing site visits and personnel information.
 * It integrates three main data streams:
 * 1. Visit Information - Details about site visits
 * 2. Persons Data - Information about all available personnel
 * 3. Site Persons Data - Mapping of personnel to specific sites
 *
 * The component provides functionality to:
 * - View existing site visits
 * - Add new site visits
 * - Add new personnel to the system
 * - Manage personnel assignments
 */

import React, { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Button,
  CircularProgress,
  Box,
  Grid,
  Divider,
  Tooltip,
} from "@mui/material";
import { useQuery } from "react-query";
import { getFormData } from "../../../services/api";
import VisitInfoDisplay from "./VisitInfoDisplay";
import AddVisitInfoDialog from "./AddVisitInfoDialog";
import AddPersonDialog from "./AddPersonDialog";
import AddIcon from "@mui/icons-material/Add";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

const VisitInfo = () => {
  // Extract station ID from URL parameters
  const { uid } = useParams();

  // State management for dialog visibility
  const [showAddVisit, setShowAddVisit] = useState(false);
  const [showAddPerson, setShowAddPerson] = useState(false);

  /**
   * Fetch visit information for the current station
   * Data includes:
   * - Visit dates
   * - Visit purposes
   * - Assigned personnel
   * - Visit notes and observations
   */
  const {
    data: visitData,
    isLoading: visitLoading,
    error: visitError,
    refetch: refetchVisits,
  } = useQuery(
    ["visitInfo", uid],
    () => getFormData(`metadata/visit-info/${uid}`),
    {
      staleTime: 5 * 60 * 1000, // Cache data for 5 minutes
    }
  );

  /**
   * Fetch all available personnel in the system
   * Used for:
   * - Assigning personnel to visits
   * - Creating new personnel records
   */
  const {
    data: personsData,
    isLoading: personsLoading,
    error: personsError,
    refetch: refetchPersons,
  } = useQuery(["persons"], () => getFormData("metadata/person"), {
    staleTime: 5 * 60 * 1000,
  });

  /**
   * Fetch site-specific personnel assignments
   * Maps personnel to specific stations/sites
   * Used to track who is assigned to which locations
   */
  const {
    data: sitePersonsData,
    isLoading: sitePersonsLoading,
    error: sitePersonsError,
  } = useQuery(
    ["sitePersons", uid],
    () => getFormData(`metadata/metadata-site-person/${uid}`),
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  // Callback to handle closing the Add Visit dialog and refresh data
  const handleAddVisitClose = useCallback(() => {
    setShowAddVisit(false);
    refetchVisits();
  }, [refetchVisits]);

  // Callback to handle closing the Add Person dialog and refresh data
  const handleAddPersonClose = useCallback(() => {
    setShowAddPerson(false);
    refetchPersons();
  }, [refetchPersons]);

  // Loading state display
  if (visitLoading || personsLoading || sitePersonsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state display
  if (visitError || personsError || sitePersonsError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography color="error">
          An error occurred while fetching data.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header section with title and action buttons */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5">Site Visits</Typography>
            <Box display="flex" gap={2}>
              {/* Button to add new personnel to the system */}
              <Tooltip title="Add new person to System">
                <Button
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setShowAddPerson(true)}
                  sx={{
                    borderColor: "black",
                    color: "black",
                    "&:hover": {
                      borderColor: "black",
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  Add Person
                </Button>
              </Tooltip>
              <Divider orientation="vertical" flexItem />
              {/* Button to add new site visit */}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowAddVisit(true)}
              >
                Add New Visit
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Main content display component */}
      <VisitInfoDisplay
        visitData={visitData}
        personsData={personsData}
        sitePersonsData={sitePersonsData}
        onRefetch={refetchVisits}
      />

      {/* Dialog for adding new visit information */}
      {showAddVisit && (
        <AddVisitInfoDialog
          open={showAddVisit}
          onClose={handleAddVisitClose}
          onSuccess={handleAddVisitClose}
          stationId={uid}
          personsData={personsData}
          onPersonAdded={refetchPersons}
        />
      )}

      {/* Dialog for adding new personnel */}
      {showAddPerson && (
        <AddPersonDialog
          open={showAddPerson}
          onClose={handleAddPersonClose}
          onSuccess={handleAddPersonClose}
        />
      )}
    </Box>
  );
};

export default VisitInfo;
